import * as React from "react";
import type { Root } from "react-dom/client";
import { Provider } from "react-redux";
import { Alert, Box } from "@amzn/awsui-components-console";
import { I18nProvider } from "@amzn/awsui-components-console/i18n";
import messages from "@amzn/awsui-components-console/i18n/messages/all.all";
import { CognitoAuth } from "amazon-cognito-auth-js";

import { store } from "./redux/store";
import App from "./app/App";
import { ensureAuthenticated } from "./auth/authenticate";
import { parseAuthInformation } from "./auth/setUserInformation";
import { MetricsPublisher } from "./metrics";
import { createAppRouter } from "./pages/router";
import { waitForFeatureFlags } from "./api/featureFlags";
import LoadingIndicator from "./components/loadingIndicator/LoadingIndicator";

export const BOOT_SPLASH_TEST_ID = "app-boot-spinner";
export const BOOT_ERROR_TEST_ID = "app-boot-error";

/**
 * Providers every boot phase renders under. Reused so the splash, the error
 * state, and the app itself all mount in the same tree shape.
 */
const BootShell = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <React.StrictMode>
    <Provider store={store}>
      <I18nProvider messages={[messages]}>
        <div className="my-app">{children}</div>
      </I18nProvider>
    </Provider>
  </React.StrictMode>
);

/**
 * `role="status"` announces the label to assistive technology once the splash
 * is revealed. The wrapper also carries the delayed fade-in (BootSplash.scss).
 */
const BootSplash = (): JSX.Element => (
  <div className="boot-splash" role="status">
    <LoadingIndicator label="Loading Claim Intake..." testId={BOOT_SPLASH_TEST_ID} />
  </div>
);

const BootError = (): JSX.Element => (
  <Box padding="l" data-testid={BOOT_ERROR_TEST_ID}>
    <Alert type="error" header="We could not sign you in">
      Reload the page to try again. If the problem continues, contact support.
    </Alert>
  </Box>
);

/**
 * Renders the application, painting a loading state for the whole boot sequence.
 *
 * Both authentication and the feature flag preload must settle before the router
 * mounts, because the router's loaders read flags and would otherwise resolve
 * them to false. That makes boot a blocking wait of up to several seconds, so
 * the splash goes up first: the alternative is an empty page for the duration.
 */
export const bootstrapApp = async (root: Root): Promise<void> => {
  const metricsPublisher = new MetricsPublisher("api.authenticate");
  const metricsPublisherError = new MetricsPublisher("api.authenticate.error");
  const metricsVisitedPublisher = new MetricsPublisher("portalVisited");
  metricsVisitedPublisher.publishVisited();

  root.render(
    <BootShell>
      <BootSplash />
    </BootShell>
  );

  try {
    const auth: CognitoAuth = await ensureAuthenticated();
    metricsPublisher.publishAuthenticationLatencyMetrics();
    metricsPublisher.publishAuthenticationSuccessMetrics();

    // Preload feature flags so they are cached before any component or route
    // loader reads them.
    //
    // The wait is bounded: past the deadline boot continues on last-known-good
    // flags (or none), and the still-running request updates every flag
    // consumer when it lands. A failure is tolerated rather than fatal, because
    // flags gate features and must not gate the whole app. Consumers that turn
    // a flag into a denial use requireFeatureFlag, which reports the
    // unavailable state instead of silently reading false.
    await waitForFeatureFlags().catch((err) =>
      console.warn("Failed to preload feature flags:", err)
    );

    /**
     * React Router definition, this needs to be defined outside the context of the React flow
     * for the React Router data API to work properly.
     * (https://reactrouter.com/en/main/routers/create-browser-router)
     */
    const user = parseAuthInformation(auth);
    const router = createAppRouter(user);
    root.render(
      <BootShell>
        <App user={user} router={router} />
      </BootShell>
    );
  } catch (err: unknown) {
    metricsPublisher.publishAuthenticationLatencyMetrics();
    metricsPublisher.publishAuthenticationFailureMetrics();
    metricsPublisherError.publishAuthenticationError(String(err));
    console.log("Authentication Error", err);

    // Replace the splash: a spinner left up forever implies progress that is
    // not coming, which reads as a hang rather than a failure.
    root.render(
      <BootShell>
        <BootError />
      </BootShell>
    );
  }
};
